import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Sidebar.scss';

/**
 * Sidebar
 * -------
 * Panel lateral que se desliza desde la derecha de la pantalla.
 *
 * Props:
 * @param {React.ReactNode} children      - Contenido que se muestra dentro del panel
 * @param {number}          defaultWidth  - Ancho inicial en % del ancho de pantalla (por defecto 35)
 * @param {boolean}         isOpen        - Controla si el sidebar está visible o no
 * @param {function}        onClose       - Función que se ejecuta cuando el usuario cierra el sidebar
 *
 * Ejemplo de uso:
 *   const [open, setOpen] = useState(false);
 *   <Sidebar isOpen={open} onClose={() => setOpen(false)} defaultWidth={40}>
 *     <p>Mi contenido aquí</p>
 *   </Sidebar>
 */
const Sidebar = ({ children, defaultWidth = 35, isOpen = false, onClose }) => {

  // ──────────────────────────────────────────────
  // ESTADOS del componente
  // ──────────────────────────────────────────────

  // Ancho actual del panel (en porcentaje del ancho de pantalla)
  const [width, setWidth] = useState(defaultWidth);

  // ¿Está el panel en modo pantalla completa?
  const [isExpanded, setIsExpanded] = useState(false);

  // ¿Está el usuario arrastrando el handle de redimensión?
  const [isDragging, setIsDragging] = useState(false);

  // ──────────────────────────────────────────────
  // REFERENCIAS (guardan valores sin causar re-renders)
  // ──────────────────────────────────────────────

  // Posición X del mouse en el momento en que empezó el arrastre
  const dragStartX = useRef(0);

  // Ancho del panel en el momento en que empezó el arrastre
  const dragStartWidth = useRef(defaultWidth);


  // ══════════════════════════════════════════════
  // LÓGICA DE ARRASTRE PARA REDIMENSIONAR
  // ══════════════════════════════════════════════

  /**
   * Paso 1: El usuario hace clic (mousedown) sobre el handle de arrastre.
   * Guardamos la posición inicial y el ancho inicial.
   */
  const handleDragStart = (e) => {
    setIsDragging(true);
    dragStartX.current     = e.clientX;
    dragStartWidth.current = width;
    e.preventDefault(); // evitar que se seleccione texto mientras se arrastra
  };

  /**
   * Paso 2: El usuario mueve el mouse (mousemove).
   * Calculamos el nuevo ancho basándonos en cuánto se movió el mouse.
   *
   * useCallback: evita recrear esta función en cada render,
   * lo que podría causar problemas con los event listeners.
   */
  const handleDragMove = useCallback((e) => {
    if (!isDragging) return;

    // Cuántos píxeles se movió el mouse (positivo = hacia la izquierda = panel más ancho)
    const deltaPixels  = dragStartX.current - e.clientX;

    // Convertir píxeles a porcentaje del ancho total de la pantalla
    const deltaPercent = (deltaPixels / window.innerWidth) * 100;

    // Nuevo ancho, limitado entre 20% (mínimo) y 90% (máximo)
    const newWidth = Math.min(90, Math.max(20, dragStartWidth.current + deltaPercent));

    setWidth(newWidth);
  }, [isDragging]);

  /**
   * Paso 3: El usuario suelta el mouse (mouseup). Terminamos el arrastre.
   */
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  /**
   * useEffect: Cuando empieza el arrastre, escuchamos eventos en TODA la ventana.
   * Así el arrastre no se interrumpe si el mouse sale del handle.
   * Cuando termina, limpiamos los listeners para no desperdiciar memoria.
   */
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup',   handleDragEnd);
    }
    // Función de limpieza: se ejecuta cuando isDragging cambia o el componente se desmonta
    return () => {
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup',   handleDragEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);


  // ══════════════════════════════════════════════
  // LÓGICA DE EXPANDIR / CERRAR
  // ══════════════════════════════════════════════

  /** Alterna entre modo pantalla completa y modo normal */
  const handleExpand = () => {
    setIsExpanded(prev => !prev);
  };

  /** Cierra el sidebar y resetea el estado */
  const handleClose = () => {
    setIsExpanded(false);
    setWidth(defaultWidth); // volver al ancho inicial
    if (onClose) onClose();
  };


  // ══════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════

  // Si el sidebar está cerrado, no renderizar nada en el DOM
  if (!isOpen) return null;

  // El ancho final que se aplica: 100vw si está expandido, o el % calculado
  const panelWidth = isExpanded ? '100vw' : `${width}vw`;

  return (
    <>
      {/* ── OVERLAY ─────────────────────────────────────────────
          Fondo semitransparente detrás del panel.
          Al hacer clic sobre él, se cierra el sidebar.
          Se oculta en modo pantalla completa.
      ───────────────────────────────────────────────────────── */}
      {!isExpanded && (
        <div
          className="sidebar-overlay"
          onClick={handleClose}
        />
      )}

      {/* ── PANEL PRINCIPAL ─────────────────────────────────────
          El panel lateral en sí. Su ancho es dinámico.
          Las clases extra modifican su comportamiento visual.
      ───────────────────────────────────────────────────────── */}
      <div
        className={[
          'sidebar',
          isExpanded ? 'sidebar--expanded' : '',
          isDragging  ? 'sidebar--dragging'  : '',
        ].join(' ')}
        style={{ width: panelWidth }}
      >

        {/* ── BARRA DE HERRAMIENTAS (toolbar) ─────────────────────
            Franja superior con los botones de control.
        ────────────────────────────────────────────────────────*/}
        <div className="sidebar__toolbar">

          {/* Handle de arrastre: arrastra para cambiar el ancho */}
          <div
            className="sidebar__drag-handle"
            onMouseDown={handleDragStart}
            title="Arrastra para cambiar el ancho"
          >
            <i className="pi pi-ellipsis-v" />
          </div>

          {/* Espacio flexible que empuja los botones hacia la derecha */}
          <div className="sidebar__toolbar-spacer" />

          {/* Botón expandir / restaurar tamaño */}
          <button
            className="sidebar__tool-btn"
            onClick={handleExpand}
            title={isExpanded ? 'Restaurar tamaño' : 'Expandir a pantalla completa'}
          >
            <i className={`pi ${isExpanded ? 'pi-window-minimize' : 'pi-window-maximize'}`} />
          </button>

          {/* Botón cerrar */}
          <button
            className="sidebar__tool-btn sidebar__tool-btn--close"
            onClick={handleClose}
            title="Cerrar"
          >
            <i className="pi pi-times" />
          </button>

        </div>
        {/* ── FIN TOOLBAR ──────────────────────────────────────── */}


        {/* ── ÁREA DE CONTENIDO ────────────────────────────────────
            Ocupa todo el espacio restante y tiene scroll vertical
            automático si el contenido es más largo que la pantalla.
        ────────────────────────────────────────────────────────*/}
        <div className="sidebar__content">
          {children}
        </div>
        {/* ── FIN CONTENIDO ────────────────────────────────────── */}

      </div>
      {/* ── FIN PANEL ────────────────────────────────────────── */}

    </>
  );
};

export default Sidebar;
